import { useMemo } from "react";

import { calculateFieldContributionStatus, isSingleFieldOutcomes, useCarbonProgramImpact, useGetAllCarbonProgramOutcomes, useInventoryImpact } from "utils";

import { FarmResponse } from "models";

import { HomeSegment, hasFieldManagementPayout } from "pages/app/home/hero/homeSegment";
import { HomePayoutSummary, summarisePayout } from "pages/app/home/status-cards/homePayoutSummary";
import { HomeFootprintingSummaryResult } from "pages/app/home/status-cards/useHomeFootprintingSummary";
import { useSoilPlus3Allotment } from "pages/app/home/status-cards/useSoilPlus3Allotment";

export const useHomePayoutSummary = (
  farm: FarmResponse | undefined,
  segment: HomeSegment,
  reportingYear: number,
  footprinting: HomeFootprintingSummaryResult,
  hasFields: boolean
): { summary: HomePayoutSummary; loading: boolean } => {
  const carbonProgram = farm?.carbonProgram ?? null;
  // the outcomes run is the expensive request: only segments with a field management payout read it, and only once there are fields
  const withFieldManagementPayout = hasFieldManagementPayout(segment);
  const {
    data: programOutcomes,
    isLoading: outcomesLoading,
    isLoadingError: outcomesError,
  } = useGetAllCarbonProgramOutcomes(farm?.id, withFieldManagementPayout && hasFields ? carbonProgram : null);
  const singleFieldOutcomes = useMemo(() => Object.values(programOutcomes ?? {}).filter(isSingleFieldOutcomes), [programOutcomes]);
  const { totalPayoutMin, totalPayoutAvg } = useCarbonProgramImpact(programOutcomes ?? {}, reportingYear);
  const inventoryImpact = useInventoryImpact(reportingYear, singleFieldOutcomes, farm?.id ?? "", carbonProgram ?? undefined);
  const { usesAllotment: useAllotmentPayout } = useSoilPlus3Allotment(farm?.id, reportingYear);
  const hasPayoutPrediction = useMemo(
    () => calculateFieldContributionStatus(programOutcomes ?? {}, reportingYear).fieldsContributingToPayouts > 0,
    [programOutcomes, reportingYear]
  );

  const summary = useMemo(
    () =>
      summarisePayout({
        segment,
        carbonProgram,
        programOutcomes,
        outcomeImpact: { totalPayoutMin, totalPayoutAvg },
        useAllotmentPayout,
        inventoryImpact,
        footprinting: { totalPayout: footprinting.summary.totalPayout, hasPayouts: footprinting.hasPayouts, unavailable: footprinting.unavailable },
        hasFields,
        hasPayoutPrediction,
        unavailable: withFieldManagementPayout && hasFields && !!outcomesError,
      }),
    [
      segment,
      carbonProgram,
      programOutcomes,
      totalPayoutMin,
      totalPayoutAvg,
      useAllotmentPayout,
      inventoryImpact,
      footprinting.summary.totalPayout,
      footprinting.hasPayouts,
      footprinting.unavailable,
      hasFields,
      hasPayoutPrediction,
      withFieldManagementPayout,
      outcomesError,
    ]
  );

  return { summary, loading: outcomesLoading || footprinting.loading };
};
